import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PropertyNamePlacesField from './PropertyNamePlacesField';
import { LOGIN_ACCENT } from './loginDarkTheme';
import {
  buildPhoneForApi,
  DEFAULT_PHONE_COUNTRY,
  getPhoneCountryOption,
  getPhoneCountryOptionLabel,
  normalizePhoneInput,
  PHONE_COUNTRY_OPTIONS,
  validateNationalPhoneByCountry,
} from '../../utils/phoneCountryUtils';
import {
  authOverlayFieldShellSx,
  freeTrialCardSx as cardSx,
  freeTrialCheckboxSx as checkboxSx,
  freeTrialFieldSx as fieldSx,
  freeTrialMiniLabelSx as miniLabelSx,
  freeTrialPrimarySx as primarySx,
  freeTrialRadioSx as radioSx,
  freeTrialSecondarySx as secondarySx,
  getAuthAutocompleteFieldSx,
  getAuthAutocompletePaperSx,
  getAuthAutocompleteSlotProps,
} from './authLightPanelTheme';

const ACCENT = LOGIN_ACCENT;
const API_URL = '/website/api/free-trial-request/';
const PROPERTY_TYPES = ['Hotel', 'Hostel', 'Resort', 'Vacation Rental', 'Apartment', 'PG'];
const ROOM_MIN = 10;
const ROOM_MAX = 250;
const SOFTWARE_OPTIONS = ['eZee', 'Cloudbeds', 'Opera', 'Mews', 'Protel', 'Other'];
const FEATURE_OPTIONS = [
  { id: 'channelManager', label: 'Channel Manager' },
  { id: 'bookingEngine', label: 'Booking Engine' },
  { id: 'pms', label: 'PMS' },
  { id: 'pos', label: 'POS' },
  { id: 'payments', label: 'Payments' },
  { id: 'analytics', label: 'Analytics' },
];
const LANGUAGES = ['English', 'Hindi'];
const HEARD_OPTIONS = ['Google', 'LinkedIn', 'Referral', 'Ads', 'Other'];

const emptyFeatures = () =>
  FEATURE_OPTIONS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {});

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phoneCountryCode: DEFAULT_PHONE_COUNTRY.code,
  phone: '',
  propertyName: '',
  propertyType: '',
  propertyPlaceSelected: false,
  placeId: '',
  country: 'India',
  state: '',
  city: '',
  address: '',
  roomRange: String(ROOM_MIN),
  usesPms: '',
  currentSoftware: '',
  features: emptyFeatures(),
  goLiveDate: '',
  preferredLanguage: 'English',
  websiteUrl: '',
  heardFrom: '',
  agreeTerms: false,
};

function normalizePhone(value) {
  return normalizePhoneInput(value);
}

function getRequestCode(data) {
  return String(data?.requestCode || '').trim();
}

function validate(step, form) {
  const errors = {};
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email || '').trim());

  if (step === 1) {
    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.email.trim()) errors.email = 'Hotel official email is required';
    else if (!emailOk) errors.email = 'Enter a valid email address';
    if (!String(form.phoneCountryCode || '').trim()) {
      errors.phoneCountryCode = 'Country code is required.';
    }
    const phoneError = validateNationalPhoneByCountry(form.phoneCountryCode, form.phone);
    if (phoneError) errors.phone = phoneError;
    if (!form.propertyName.trim()) errors.propertyName = 'Property name is required';
    if (!form.propertyType) errors.propertyType = 'Select property type';
  }

  if (step === 2) {
    if (!form.usesPms) errors.usesPms = 'Please select an option';
    if (form.usesPms === 'yes' && !form.currentSoftware) errors.currentSoftware = 'Select current software';
    if (form.usesPms === 'yes' && !Object.values(form.features).some(Boolean)) {
      errors.features = 'Pick at least one feature';
    }
    if (!form.goLiveDate) errors.goLiveDate = 'Pick expected go-live date';
    if (!form.heardFrom) errors.heardFrom = 'Select how you heard about us';
    if (!form.agreeTerms) errors.agreeTerms = 'Please agree to continue';
  }

  return errors;
}

function matchCountryOption(countryLong) {
  const raw = String(countryLong || '').trim();
  if (!raw) return 'India';
  if (raw.toLowerCase() === 'india') return 'India';
  return raw;
}

function buildPayload(form) {
  const phone = buildPhoneForApi(form.phoneCountryCode, form.phone);
  const propertyName = form.propertyName.trim();
  const safeAddress =
    String(form.address || '').trim() ||
    [form.city, form.state, form.country].map((value) => String(value || '').trim()).filter(Boolean).join(', ');
  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim().toLowerCase(),
    phone,
    propertyName,
    propertyType: form.propertyType,
    roomRange: form.roomRange,
    country: form.country || 'India',
    state: form.state || 'Not specified',
    city: form.city || 'Not specified',
    address: safeAddress || propertyName || 'Not specified',
    usesPms: form.usesPms,
    currentSoftware: form.usesPms === 'yes' ? form.currentSoftware : '',
    features: form.usesPms === 'yes' ? form.features : emptyFeatures(),
    goLiveDate: form.goLiveDate || null,
    preferredLanguage: form.preferredLanguage,
    websiteUrl: form.websiteUrl.trim(),
    heardFrom: form.heardFrom,
    agreeTerms: Boolean(form.agreeTerms),
    source: 'console-login',
  };
}

export function LoginFreeTrialPanel({ seedEmail = '', onBackToLogin, light = false, compact = false, hideHeader = false }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...initialForm, email: seedEmail || '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const roomCount = Number(form.roomRange) || ROOM_MIN;
  const canSubmit = useMemo(() => {
    return Object.keys({ ...validate(1, form), ...validate(2, form) }).length === 0;
  }, [form]);

  const update = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      return next;
    });
    setApiError('');
  };

  const handleNext = () => {
    const nextErrors = validate(1, form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setStep(2);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = { ...validate(1, form), ...validate(2, form) };
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStep(Object.keys(validate(1, form)).length ? 1 : 2);
      return;
    }

    setSubmitting(true);
    setApiError('');
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(buildPayload(form)),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const firstFieldError = Object.values(data || {}).flat?.()?.[0];
        throw new Error(data.detail || data.message || firstFieldError || `Free trial request failed (${response.status})`);
      }

      setSuccess({
        title: data.success === false ? 'Request received' : 'Your free trial account is ready',
        message: data.message || 'Your RoomNexa login credentials have been sent to your hotel official email.',
        requestCode: getRequestCode(data),
        trialEnabled: Boolean(data.trialEnabled),
      });
    } catch (error) {
      setApiError(error.message || 'Could not submit free trial request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const surfaceSx = light
    ? {
        bgcolor: 'transparent',
        color: '#111827',
        borderRadius: 0,
        overflow: 'visible',
        border: 'none',
        boxShadow: 'none',
        backgroundImage: 'none',
      }
    : {
        bgcolor: '#090909',
        color: '#fff',
        borderRadius: { xs: 0, sm: 4 },
        overflow: compact ? 'visible' : 'hidden',
        border: { xs: 'none', sm: '1px solid rgba(255,255,255,0.12)' },
        boxShadow: { xs: 'none', sm: '0 24px 80px rgba(0,0,0,0.36)' },
        backgroundImage:
          'radial-gradient(circle at 12% 10%, rgba(76,225,182,0.16), transparent 30%), radial-gradient(circle at 90% 8%, rgba(45,212,191,0.08), transparent 28%)',
      };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        height: light ? 'auto' : '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        ...surfaceSx,
      }}
    >
      <Box sx={{ px: light ? 0 : { xs: 2.25, sm: 4 }, pt: light ? 0 : { xs: 2.5, sm: 3 }, pb: hideHeader ? 0 : compact ? 1 : 1.5 }}>
        {onBackToLogin ? (
          <Button
            type="button"
            onClick={onBackToLogin}
            startIcon={<ArrowBackIcon />}
            sx={{
              mb: 2,
              px: 0,
              color: light ? '#111827' : 'rgba(255,255,255,0.78)',
              textTransform: 'none',
              fontWeight: 800,
              '&:hover': { bgcolor: 'transparent', color: light ? '#0f766e' : ACCENT },
            }}
          >
            Back to Login
          </Button>
        ) : null}
        {!hideHeader ? (
          <Stack alignItems="center" spacing={compact ? 0.75 : 1}>
            <Box sx={{ width: compact ? 38 : 46, height: compact ? 38 : 46, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(ACCENT, 0.16), color: ACCENT }}>
              <RocketLaunchIcon sx={{ fontSize: compact ? 20 : 24 }} />
            </Box>
            <Typography component="h1" sx={{ color: light ? '#111827' : '#fff', fontSize: { xs: '1.35rem', sm: compact ? '1.45rem' : '1.8rem' }, fontWeight: 900, letterSpacing: '-0.03em', textAlign: 'center' }}>
              Start Your Free 30-Day Trial
            </Typography>
            <Typography sx={{ color: light ? '#4b5563' : 'rgba(226,232,240,0.72)', fontWeight: 700, fontSize: compact ? '0.84rem' : '0.92rem' }}>
              No credit card required · Step {step} of 2
            </Typography>
            <StepDots step={step} light={light} compact={compact} />
          </Stack>
        ) : null}
      </Box>

      <Box sx={{ px: light ? 0 : { xs: 2.25, sm: 4 }, py: compact ? 0.5 : 1, flex: light ? 'initial' : 1, minHeight: 0, overflowY: light ? 'visible' : 'auto', WebkitOverflowScrolling: 'touch', ...authOverlayFieldShellSx }}>
        {apiError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.12)', color: '#fecaca', '& .MuiAlert-icon': { color: '#f87171' } }}>
            {apiError}
          </Alert>
        ) : null}

        {success ? (
          <SuccessState success={success} light={light} />
        ) : step === 1 ? (
          <StepOne form={form} errors={errors} update={update} roomCount={roomCount} light={light} compact={compact} />
        ) : (
          <StepTwo form={form} errors={errors} update={update} light={light} compact={compact} />
        )}
      </Box>

      <Box sx={{ px: light ? 0 : { xs: 2.25, sm: 4 }, pt: compact ? 1.25 : 2.5, pb: light ? 0 : 2.5, borderTop: light ? 'none' : '1px solid rgba(255,255,255,0.08)', bgcolor: light ? 'transparent' : 'rgba(9,9,9,0.92)' }}>
        {success ? (
          <Button onClick={onBackToLogin} fullWidth variant="contained" sx={primarySx(compact)}>
            Back to Login
          </Button>
        ) : (
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
            {step === 2 ? (
              <Button type="button" onClick={() => setStep(1)} startIcon={<ArrowBackIcon />} variant="outlined" sx={secondarySx(light, compact)}>
                Back
              </Button>
            ) : null}
            {step === 1 ? (
              <Button type="button" onClick={handleNext} fullWidth variant="contained" sx={primarySx(compact)}>
                Continue
              </Button>
            ) : (
              <Button type="submit" fullWidth variant="contained" disabled={submitting || !canSubmit} sx={primarySx(compact)}>
                {submitting ? 'Creating account...' : 'Start Free Trial'}
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export default function LoginFreeTrialDialog({ open, onClose, seedEmail = '' }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...initialForm, email: seedEmail || '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const roomCount = Number(form.roomRange) || ROOM_MIN;
  const canSubmit = useMemo(() => {
    return Object.keys({ ...validate(1, form), ...validate(2, form) }).length === 0;
  }, [form]);

  const update = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      return next;
    });
    setApiError('');
  };

  const handleClose = () => {
    setStep(1);
    setForm({ ...initialForm, email: seedEmail || '' });
    setErrors({});
    setApiError('');
    setSubmitting(false);
    setSuccess(null);
    onClose?.();
  };

  const handleNext = () => {
    const nextErrors = validate(1, form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setStep(2);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = { ...validate(1, form), ...validate(2, form) };
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStep(Object.keys(validate(1, form)).length ? 1 : 2);
      return;
    }

    setSubmitting(true);
    setApiError('');
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(buildPayload(form)),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const firstFieldError = Object.values(data || {}).flat?.()?.[0];
        throw new Error(data.detail || data.message || firstFieldError || `Free trial request failed (${response.status})`);
      }

      setSuccess({
        title: data.success === false ? 'Request received' : 'Your free trial account is ready',
        message: data.message || 'Your RoomNexa login credentials have been sent to your hotel official email.',
        requestCode: getRequestCode(data),
        trialEnabled: Boolean(data.trialEnabled),
      });
    } catch (error) {
      setApiError(error.message || 'Could not submit free trial request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          bgcolor: '#090909',
          color: '#fff',
          overflow: 'visible',
          maxHeight: fullScreen ? '100dvh' : 'calc(100dvh - 32px)',
          border: fullScreen ? 'none' : '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 28px 100px rgba(0,0,0,0.6)',
          backgroundImage:
            'radial-gradient(circle at 12% 10%, rgba(76,225,182,0.18), transparent 30%), radial-gradient(circle at 90% 8%, rgba(45,212,191,0.10), transparent 28%)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: 'rgba(2,6,23,0.76)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          },
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ height: fullScreen ? '100%' : '82vh', display: 'flex', flexDirection: 'column' }}>
        <DialogTitle sx={{ px: { xs: 2.25, sm: 4 }, pt: { xs: 2.5, sm: 3 }, pb: 1.5, position: 'relative' }}>
          <IconButton
            aria-label="Close free trial"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 14,
              right: 14,
              color: 'rgba(255,255,255,0.82)',
              border: '1px solid rgba(255,255,255,0.16)',
              '&:hover': { color: '#fff', borderColor: ACCENT, bgcolor: 'rgba(76,225,182,0.08)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Stack alignItems="center" spacing={1}>
            <Box sx={{ width: 46, height: 46, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(ACCENT, 0.16), color: ACCENT }}>
              <RocketLaunchIcon />
            </Box>
            <Typography component="h2" sx={{ fontSize: { xs: '1.45rem', sm: '1.8rem' }, fontWeight: 900, letterSpacing: '-0.03em' }}>
              Free Trial Setup
            </Typography>
            <Typography sx={{ color: 'rgba(226,232,240,0.72)', fontWeight: 600, fontSize: '0.92rem' }}>
              No credit card required · Step {step} of 2
            </Typography>
            <StepDots step={step} />
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2.25, sm: 4 },
            py: 1,
            flex: 1,
            overflowY: 'auto',
            overflowX: 'visible',
            WebkitOverflowScrolling: 'touch',
            ...authOverlayFieldShellSx,
          }}
        >
          {apiError ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.12)', color: '#fecaca', '& .MuiAlert-icon': { color: '#f87171' } }}>
              {apiError}
            </Alert>
          ) : null}

          {success ? (
            <SuccessState success={success} light />
          ) : step === 1 ? (
            <StepOne form={form} errors={errors} update={update} roomCount={roomCount} />
          ) : (
            <StepTwo form={form} errors={errors} update={update} />
          )}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2.25, sm: 4 }, py: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(9,9,9,0.92)' }}>
          {success ? (
            <Button onClick={handleClose} fullWidth variant="contained" sx={primarySx()}>
              Back to Login
            </Button>
          ) : (
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
              {step === 2 ? (
                <Button type="button" onClick={() => setStep(1)} startIcon={<ArrowBackIcon />} variant="outlined" sx={secondarySx()}>
                  Back
                </Button>
              ) : null}
              {step === 1 ? (
                <Button type="button" onClick={handleNext} fullWidth variant="contained" sx={primarySx()}>
                  Continue
                </Button>
              ) : (
                <Button type="submit" fullWidth variant="contained" disabled={submitting || !canSubmit} sx={primarySx()}>
                  {submitting ? 'Creating account...' : 'Start Free Trial'}
                </Button>
              )}
            </Stack>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function StepDots({ step, light = false }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 1 }}>
      {[1, 2].map((number, index) => {
        const active = step === number;
        const done = step > number;
        return (
          <React.Fragment key={number}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                border: `2px solid ${active || done ? ACCENT : light ? 'rgba(17,24,39,0.16)' : 'rgba(255,255,255,0.18)'}`,
                bgcolor: done ? ACCENT : active ? alpha(ACCENT, 0.15) : 'transparent',
                color: done ? '#04120e' : active ? ACCENT : light ? '#6b7280' : 'rgba(255,255,255,0.42)',
                fontWeight: 900,
              }}
            >
              {done ? <CheckIcon sx={{ fontSize: 18 }} /> : number}
            </Box>
            {index === 0 ? <Box sx={{ width: 28, height: 2, borderRadius: 1, bgcolor: step > 1 ? ACCENT : light ? 'rgba(17,24,39,0.14)' : 'rgba(255,255,255,0.12)' }} /> : null}
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

function StepOne({ form, errors, update, roomCount, light = false, compact = false }) {
  return (
    <Stack spacing={compact ? 1.25 : 2.1}>
      <SectionLabel light={light}>Contact</SectionLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <TrialTextField light={light} compact={compact} label="First Name" value={form.firstName} onChange={(e) => update({ firstName: e.target.value })} errorText={errors.firstName} />
        <TrialTextField light={light} compact={compact} label="Last Name" value={form.lastName} onChange={(e) => update({ lastName: e.target.value })} errorText={errors.lastName} />
      </Box>
      <TrialTextField light={light} compact={compact} label="Hotel Official Email" type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} errorText={errors.email} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '0.95fr 1.05fr' }, gap: 1.5 }}>
        <PhoneCountryCodeField
          light={light}
          compact={compact}
          value={form.phoneCountryCode}
          onChange={(code) => update({ phoneCountryCode: code, phone: normalizePhone(form.phone) })}
          errorText={errors.phoneCountryCode}
        />
        <TrialTextField
          light={light}
          compact={compact}
          label="Phone Number"
          value={form.phone}
          onChange={(e) => update({ phone: normalizePhone(e.target.value) })}
          errorText={errors.phone}
          inputProps={{ inputMode: 'numeric' }}
        />
      </Box>

      <SectionLabel light={light}>Property</SectionLabel>
      <PropertyNamePlacesField
        light={light}
        compact={compact}
        allowManualEntry
        value={form.propertyName}
        onChange={(value) =>
          update({
            propertyName: value,
            propertyPlaceSelected: false,
            placeId: '',
            state: '',
            city: '',
            address: '',
            country: 'India',
          })
        }
        onPlaceSelect={(place) =>
          update({
            propertyName: place.propertyName,
            propertyPlaceSelected: true,
            placeId: place.placeId || '',
            state: place.state || '',
            city: place.city || '',
            address: place.address || place.formattedAddress || place.shortFormattedAddress || '',
            country: matchCountryOption(place.countryLong),
          })
        }
        error={Boolean(errors.propertyName)}
        helperText={errors.propertyName || ''}
        sx={fieldSx(light, compact)}
      />
      <Box sx={cardSx(errors.propertyType, light, compact)}>
        <Typography component="span" sx={miniLabelSx(light)}>Property Type</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {PROPERTY_TYPES.map((type) => {
            const selected = form.propertyType === type;
            return (
              <Chip
                key={type}
                label={type}
                clickable
                onClick={() => update({ propertyType: type })}
                sx={{
                  borderRadius: 999,
                  color: selected ? '#04120e' : light ? '#374151' : 'rgba(255,255,255,0.86)',
                  bgcolor: selected ? ACCENT : light ? '#ffffff' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${selected ? ACCENT : light ? 'rgba(17,24,39,0.16)' : 'rgba(255,255,255,0.14)'}`,
                  fontWeight: 800,
                }}
              />
            );
          })}
        </Stack>
        {errors.propertyType ? <ErrorText>{errors.propertyType}</ErrorText> : null}
      </Box>
      <Box sx={cardSx(errors.roomRange, light, compact)}>
        <Stack direction="row" justifyContent="space-between">
          <Typography component="span" sx={miniLabelSx(light)}>Number of Rooms</Typography>
          <Typography component="span" sx={{ fontWeight: 900, color: light ? '#111827' : '#fff' }}>{roomCount} rooms</Typography>
        </Stack>
        <Box component="input" type="range" min={ROOM_MIN} max={ROOM_MAX} value={roomCount} onChange={(e) => update({ roomRange: String(e.target.value) })} sx={{ width: '100%', accentColor: ACCENT, mt: 1 }} />
        <Typography component="span" sx={{ color: light ? '#4b5563' : 'rgba(255,255,255,0.5)', fontSize: '0.8rem', display: 'block' }}>Selected rooms: {roomCount}</Typography>
      </Box>
    </Stack>
  );
}

function StepTwo({ form, errors, update, light = false, compact = false }) {
  const toggleFeature = (feature) => {
    update({ features: { ...form.features, [feature]: !form.features[feature] } });
  };

  return (
    <Stack spacing={compact ? 1.25 : 2.1}>
      <SectionLabel light={light}>Business Setup</SectionLabel>
      <FormControl error={Boolean(errors.usesPms)}>
        <FormLabel sx={{ color: light ? '#374151' : 'rgba(255,255,255,0.76)', mb: 1, fontWeight: 700 }}>Are you currently using hotel management software?</FormLabel>
        <RadioGroup value={form.usesPms} onChange={(e) => update({ usesPms: e.target.value })}>
          <FormControlLabel value="yes" control={<Radio sx={radioSx(light)} />} label={<Typography component="span" sx={{ color: light ? '#111827' : '#fff' }}>Yes</Typography>} />
          <FormControlLabel value="no" control={<Radio sx={radioSx(light)} />} label={<Typography component="span" sx={{ color: light ? '#111827' : '#fff' }}>No</Typography>} />
        </RadioGroup>
        {errors.usesPms ? <ErrorText>{errors.usesPms}</ErrorText> : null}
      </FormControl>

      {form.usesPms === 'yes' ? (
        <>
          <TrialOptionAutocomplete
            light={light}
            compact={compact}
            label="Current Software"
            placeholder="Search software"
            value={form.currentSoftware}
            onChange={(value) => update({ currentSoftware: value })}
            options={SOFTWARE_OPTIONS}
            errorText={errors.currentSoftware}
          />
          <Box sx={cardSx(errors.features, light, compact)}>
            <Typography component="span" sx={miniLabelSx(light)}>Interested Features</Typography>
            <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.2 }}>
              {FEATURE_OPTIONS.map((item) => (
                <FormControlLabel
                  key={item.id}
                  control={<Checkbox checked={Boolean(form.features[item.id])} onChange={() => toggleFeature(item.id)} sx={checkboxSx(light)} />}
                  label={<Typography component="span" sx={{ color: light ? '#374151' : 'rgba(255,255,255,0.88)', fontSize: '0.9rem' }}>{item.label}</Typography>}
                />
              ))}
            </FormGroup>
            {errors.features ? <ErrorText>{errors.features}</ErrorText> : null}
          </Box>
        </>
      ) : null}

      <SectionLabel light={light}>Finish</SectionLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <TrialTextField light={light} compact={compact} label="Expected Go Live Date" type="date" value={form.goLiveDate} onChange={(e) => update({ goLiveDate: e.target.value })} errorText={errors.goLiveDate} InputLabelProps={{ shrink: true }} />
        <TrialOptionAutocomplete
          light={light}
          compact={compact}
          label="Preferred Language"
          placeholder="Search language"
          value={form.preferredLanguage}
          onChange={(value) => update({ preferredLanguage: value })}
          options={LANGUAGES}
        />
      </Box>
      <TrialTextField light={light} compact={compact} label="Website URL (optional)" value={form.websiteUrl} onChange={(e) => update({ websiteUrl: e.target.value })} />
      <TrialOptionAutocomplete
        light={light}
        compact={compact}
        label="How did you hear about us?"
        placeholder="Search source"
        value={form.heardFrom}
        onChange={(value) => update({ heardFrom: value })}
        options={HEARD_OPTIONS}
        errorText={errors.heardFrom}
      />
      <Box sx={cardSx(errors.agreeTerms, light, compact)}>
        <FormControlLabel
          control={<Checkbox checked={form.agreeTerms} onChange={(e) => update({ agreeTerms: e.target.checked })} sx={checkboxSx(light)} />}
          label={<Typography component="span" sx={{ color: light ? '#374151' : 'rgba(255,255,255,0.78)', fontSize: '0.9rem' }}>I agree to RoomNexa Terms and Privacy Policy.</Typography>}
        />
        {errors.agreeTerms ? <ErrorText>{errors.agreeTerms}</ErrorText> : null}
      </Box>
    </Stack>
  );
}

function SuccessState({ success, light = false }) {
  const titleColor = light ? '#000000' : '#ffffff';
  const messageColor = light ? '#000000' : 'rgba(226,232,240,0.72)';
  const codeColor = light ? '#000000' : ACCENT;

  return (
    <Box className="free-trial-success-state" sx={{ py: { xs: 5, sm: 7 }, textAlign: 'center' }}>
      <Box sx={{ width: 76, height: 76, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: alpha(ACCENT, 0.18), color: ACCENT, border: `1px solid ${alpha(ACCENT, 0.4)}` }}>
        <CheckIcon sx={{ fontSize: 42 }} />
      </Box>
      <Typography
        component="div"
        className="free-trial-success-title"
        sx={{
          color: `${titleColor} !important`,
          fontSize: { xs: '1.45rem', sm: '1.85rem' },
          fontWeight: 900,
          mb: 1,
          lineHeight: 1.25,
        }}
      >
        {success.title}
      </Typography>
      <Typography
        component="div"
        className="free-trial-success-message"
        sx={{
          color: `${messageColor} !important`,
          maxWidth: 520,
          mx: 'auto',
          lineHeight: 1.7,
          fontSize: '0.95rem',
          fontWeight: 600,
        }}
      >
        {success.message}
      </Typography>
      {success.requestCode ? (
        <Typography
          component="div"
          className="free-trial-success-code"
          sx={{
            mt: 2,
            color: `${codeColor} !important`,
            fontWeight: 800,
            fontSize: '0.9rem',
            letterSpacing: '0.02em',
          }}
        >
          Request code: {success.requestCode}
        </Typography>
      ) : null}
    </Box>
  );
}

function TrialTextField({ errorText = '', sx, light = false, compact = false, ...props }) {
  return (
    <TextField
      fullWidth
      error={Boolean(errorText)}
      helperText={errorText}
      sx={{ ...fieldSx(light, compact), ...sx }}
      {...props}
    />
  );
}

function TrialOptionAutocomplete({
  label,
  value,
  onChange,
  options,
  errorText = '',
  helperText = '',
  placeholder = 'Search',
  light = false,
  compact = false,
}) {
  const autocompleteSlotProps = useMemo(
    () => getAuthAutocompleteSlotProps(light, { paperSx: getAuthAutocompletePaperSx(light) }),
    [light]
  );

  return (
    <Autocomplete
      options={options}
      value={value || null}
      onChange={(_event, nextValue) => onChange(nextValue || '')}
      getOptionLabel={(option) => String(option || '')}
      isOptionEqualToValue={(a, b) => a === b}
      filterOptions={(opts, state) => {
        const search = String(state.inputValue || '').trim().toLowerCase();
        if (!search) return opts;
        return opts.filter((option) => String(option).toLowerCase().includes(search));
      }}
      sx={getAuthAutocompleteFieldSx(light, compact)}
      slotProps={autocompleteSlotProps}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={Boolean(errorText)}
          helperText={errorText || helperText || ''}
        />
      )}
    />
  );
}

function PhoneCountryCodeField({ value, onChange, errorText = '', light = false, compact = false }) {
  const selected = getPhoneCountryOption(value);
  const autocompleteSlotProps = useMemo(
    () => getAuthAutocompleteSlotProps(light, { paperSx: getAuthAutocompletePaperSx(light) }),
    [light]
  );

  return (
    <Autocomplete
      disableClearable
      options={PHONE_COUNTRY_OPTIONS}
      value={selected}
      isOptionEqualToValue={(option, current) => option.code === current.code}
      getOptionLabel={getPhoneCountryOptionLabel}
      filterOptions={(options, state) => {
        const search = String(state.inputValue || '').trim().toLowerCase();
        if (!search) return options;
        return options.filter((option) =>
          `${option.name} ${option.dialCode} ${option.code}`.toLowerCase().includes(search)
        );
      }}
      onChange={(_event, nextOption) => {
        if (!nextOption) return;
        onChange(nextOption.code);
      }}
      sx={getAuthAutocompleteFieldSx(light, compact)}
      slotProps={autocompleteSlotProps}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Country code"
          error={Boolean(errorText)}
          helperText={errorText || ''}
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Country and dial code',
          }}
        />
      )}
    />
  );
}

function SectionLabel({ children, light = false }) {
  return (
    <Typography
      component="span"
      sx={{
        display: 'block',
        color: light ? '#111827' : ACCENT,
        fontWeight: 900,
        fontSize: '0.78rem',
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
  );
}

function ErrorText({ children }) {
  return <Typography component="span" sx={{ color: '#fca5a5', fontSize: '0.78rem', mt: 0.8, display: 'block' }}>{children}</Typography>;
}
